import { deleteJson, getJson, postJson } from "./apiClient";
import { hideLoginView, renderLoginMenuItem, renderLoginView, showLoginView as showCredentialsLoginView } from "./credentialsView";
import { getElement, setBusy, showError } from "./dom";
import { renderCreateSuccess, renderExtraction } from "./extractionView";
import { numericValueFrom, valueFrom } from "./formatters";
import { collectLookupAddresses, getActiveEmailContext, openOutlookCompose } from "./officeContext";
import { hasNoPartnerMatches, renderResults, showNoExternalLookupAddressNotice, showNoPartnerMatchNotice } from "./resultsView";
import "./taskpane.css";
let currentEmail;
let currentExtraction;
let credentialStatus;
let isCreatingPartners = false;
let partnerCategoriesPromise;
const resultsViewActions = {
    onCreateSalesOrder: (partnerId, button) => void createSalesOrder(partnerId, button),
    loadPartnerCategories: loadPartnerCategories,
    onAddCompanyTag: (companyId, categoryId, select) => void addCompanyTag(companyId, categoryId, select),
    onEmailCompose: openOutlookCompose,
    onEnableMarketing: (companyId, input) => void enableMarketing(companyId, input),
    onMergeDuplicates: (destinationId, sourceIds, button) => void smartMergeDuplicates(destinationId, sourceIds, button),
    onSetDecisionMaker: (contactId, button) => void setContactDecisionMaker(contactId, button)
};
Office.onReady(() => {
    getElement("search-button").addEventListener("click", () => void searchOdoo());
    getElement("extract-button").addEventListener("click", () => void extractCompanyContact());
    getElement("login-menu-item").addEventListener("click", () => showCredentialsLoginView(credentialStatus, () => void saveOdooCredentials()));
    getElement("disconnect-menu-item").addEventListener("click", () => void disconnectOdooCredentials());
    void loadCredentialStatus();
    Office.context.mailbox?.addHandlerAsync(Office.EventType.ItemChanged, () => void refreshForCurrentEmail(true));
    void refreshForCurrentEmail(true);
});
async function refreshForCurrentEmail(autoSearch) {
    try {
        currentEmail = undefined;
        currentExtraction = undefined;
        isCreatingPartners = false;
        getElement("results").innerHTML = "";
        getElement("extraction").innerHTML = "";
        getElement("extract-button").disabled = true;
        currentEmail = await getActiveEmailContext();
        getElement("message-summary").textContent = currentEmail.subject || "No subject";
        getElement("status").textContent = autoSearch ? "Email changed. Searching Odoo..." : "Ready to search Odoo.";
        if (autoSearch && (await hasOdooCredentials())) {
            await searchOdoo();
        }
        else if (autoSearch) {
            getElement("status").textContent = "Enter your Odoo user and API key before searching.";
        }
    }
    catch (error) {
        showError(error);
    }
}
async function searchOdoo() {
    try {
        const email = await ensureEmailContext();
        await ensureOdooCredentials();
        setBusy("Searching Odoo records...");
        const results = await postJson("/api/email/search", email);
        getElement("extract-button").disabled = false;
        if (hasNoPartnerMatches(results)) {
            const lookupAddresses = collectLookupAddresses(email);
            if (lookupAddresses.length === 0) {
                showNoExternalLookupAddressNotice();
                getElement("status").textContent = "No external email address found for Odoo lookup.";
                return;
            }
            showNoPartnerMatchNotice(email);
            getElement("status").textContent = "No Odoo match found. Use Extract company/contact only if this email should become an Odoo record.";
            return;
        }
        renderResults(results, resultsViewActions);
        getElement("status").textContent = "Odoo search complete.";
    }
    catch (error) {
        showError(error);
    }
}
async function extractCompanyContact(statusMessage = "Extracting company and contact details...") {
    try {
        const email = await ensureEmailContext();
        setBusy(statusMessage);
        currentExtraction = await postJson("/api/email/extract-contact", email);
        renderExtraction(currentExtraction, () => void createPartners());
        getElement("status").textContent = "Review extracted details before creating records.";
    }
    catch (error) {
        showError(error);
    }
}
async function createPartners() {
    if (isCreatingPartners) {
        return;
    }
    let createButton;
    try {
        if (!currentExtraction) {
            throw new Error("Run extraction before creating Odoo records.");
        }
        await ensureOdooCredentials();
        isCreatingPartners = true;
        const form = getElement("create-partner-form");
        createButton = getElement("create-button");
        createButton.disabled = true;
        createButton.textContent = "Creating in Odoo...";
        const formData = new FormData(form);
        const request = {
            company: {
                city: valueFrom(formData, "company.city"),
                email: valueFrom(formData, "company.email"),
                name: valueFrom(formData, "company.name"),
                phone: valueFrom(formData, "company.phone"),
                street: valueFrom(formData, "company.street"),
                vat: valueFrom(formData, "company.vat"),
                website: valueFrom(formData, "company.website"),
                zip: valueFrom(formData, "company.zip")
            },
            contact: {
                email: valueFrom(formData, "contact.email"),
                function: valueFrom(formData, "contact.function"),
                mobile: valueFrom(formData, "contact.mobile"),
                name: valueFrom(formData, "contact.name"),
                phone: valueFrom(formData, "contact.phone")
            },
            createCompany: formData.get("createCompany") === "on",
            createContact: formData.get("createContact") === "on",
            parentCompanyId: numericValueFrom(formData, "parentCompanyId")
        };
        setBusy("Creating reviewed Odoo records...");
        const result = await postJson("/api/odoo/partners", request);
        renderCreateSuccess(result, request);
        const refreshedResults = await postJson("/api/email/search", await ensureEmailContext());
        renderResults(refreshedResults, resultsViewActions);
        getElement("status").textContent = `Created company ID ${result.companyId ?? "not created"}, contact ID ${result.contactId ?? "not created"}. Loaded matching Odoo records.`;
    }
    catch (error) {
        isCreatingPartners = false;
        if (createButton) {
            createButton.disabled = false;
            createButton.textContent = "Create Odoo contact / company";
        }
        showError(error);
    }
}
async function enableMarketing(companyId, input) {
    try {
        await ensureOdooCredentials();
        input.disabled = true;
        setBusy("Enabling marketing in Odoo...");
        await postJson("/api/odoo/companies/enable-marketing", { companyId });
        input.checked = true;
        input.closest(".marketing-switch")?.classList.add("is-enabled");
        getElement("status").textContent = "Marketing enabled for company.";
    }
    catch (error) {
        input.checked = false;
        input.disabled = false;
        showError(error);
    }
}
async function addCompanyTag(companyId, categoryId, select) {
    try {
        await ensureOdooCredentials();
        select.disabled = true;
        setBusy("Updating company tag in Odoo...");
        await postJson("/api/odoo/companies/category", { categoryId, companyId });
        select.closest(".quality-check")?.remove();
        getElement("status").textContent = "Company tag updated.";
    }
    catch (error) {
        select.disabled = false;
        select.value = "";
        showError(error);
    }
}
async function setContactDecisionMaker(contactId, button) {
    try {
        await ensureOdooCredentials();
        button.disabled = true;
        setBusy("Setting decision maker in Odoo...");
        await postJson("/api/odoo/contacts/decision-maker", { contactId });
        const card = button.closest(".contact-record");
        card?.classList.add("is-decision-maker");
        card?.querySelector(".decision-maker-icon")?.classList.remove("is-hidden");
        getElement("status").textContent = "Decision maker updated.";
    }
    catch (error) {
        button.disabled = false;
        showError(error);
    }
}
async function smartMergeDuplicates(destinationId, sourceIds, button) {
    try {
        await ensureOdooCredentials();
        button.disabled = true;
        button.textContent = "Merging duplicates...";
        setBusy("Running Odoo smart merge...");
        const result = await postJson("/api/odoo/partners/smart-merge", { destinationId, sourceIds });
        if (!result.ok) {
            throw new Error(result.error || "Odoo smart merge did not complete.");
        }
        const refreshedResults = await postJson("/api/email/search", await ensureEmailContext());
        renderResults(refreshedResults, resultsViewActions);
        getElement("status").textContent = "Odoo smart merge complete.";
    }
    catch (error) {
        button.disabled = false;
        button.textContent = "Smart merge suggested duplicates";
        showError(error);
    }
}
async function ensureEmailContext() {
    currentEmail = currentEmail ?? (await getActiveEmailContext());
    return currentEmail;
}
async function hasOdooCredentials() {
    credentialStatus = credentialStatus ?? (await getJson("/api/credentials/status"));
    renderLoginMenuItem(credentialStatus);
    return credentialStatus.connected;
}
async function ensureOdooCredentials() {
    if (!(await hasOdooCredentials())) {
        throw new Error("Enter your Odoo user and API key before searching Odoo.");
    }
}
async function createSalesOrder(partnerId, button) {
    const targetWindow = window.open("about:blank", "_blank");
    const originalText = button.textContent;
    try {
        await ensureOdooCredentials();
        button.disabled = true;
        button.textContent = "...";
        setBusy("Creating sales order in Odoo...");
        const result = await postJson("/api/odoo/sales-orders", { partnerId });
        if (targetWindow) {
            targetWindow.location.href = result.url;
        }
        else {
            window.open(result.url, "_blank", "noopener");
        }
        const refreshedResults = await postJson("/api/email/search", await ensureEmailContext());
        renderResults(refreshedResults, resultsViewActions);
        getElement("status").textContent = `Created sales order #${result.id}.`;
    }
    catch (error) {
        targetWindow?.close();
        button.disabled = false;
        button.textContent = originalText;
        showError(error);
    }
}
async function loadCredentialStatus() {
    try {
        credentialStatus = await getJson("/api/credentials/status");
        renderLoginMenuItem(credentialStatus);
    }
    catch (error) {
        getElement("status").textContent = error instanceof Error ? error.message : "Could not load Odoo credential status.";
    }
}
async function saveOdooCredentials() {
    try {
        const form = getElement("credentials-form");
        const saveButton = getElement("credentials-save-button");
        const formData = new FormData(form);
        const request = {
            odooApiToken: valueFrom(formData, "odooApiToken") ?? "",
            odooUser: valueFrom(formData, "odooUser") ?? ""
        };
        saveButton.disabled = true;
        saveButton.textContent = "Verifying...";
        credentialStatus = await postJson("/api/credentials", request);
        renderLoginMenuItem(credentialStatus);
        hideLoginView();
        getElement("status").textContent = `Connected to Odoo as ${credentialStatus.odooUser}.`;
    }
    catch (error) {
        renderLoginView(credentialStatus, () => void saveOdooCredentials());
        showError(error);
    }
}
async function disconnectOdooCredentials() {
    try {
        getElement("app-menu").open = false;
        await deleteJson("/api/credentials");
        credentialStatus = { connected: false };
        partnerCategoriesPromise = undefined;
        getElement("results").innerHTML = "";
        getElement("extraction").innerHTML = "";
        renderLoginMenuItem(credentialStatus);
        hideLoginView();
        getElement("status").textContent = "Odoo credentials removed.";
    }
    catch (error) {
        showError(error);
    }
}
function loadPartnerCategories() {
    partnerCategoriesPromise = partnerCategoriesPromise ?? getJson("/api/odoo/partner-categories");
    return partnerCategoriesPromise;
}
